import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { expandNodeIdsWithGroupMembers } from '../../lib/diagram/grouping';
import { getShapeManifest } from '../../lib/diagram/registries';
import { BPMN_SHAPES } from '../../domains/process/shapes/bpmn/BpmnShapes.defs';
import { AI_FLOWCHART_SHAPES } from '../../domains/process/shapes/flowchart/FlowchartShapes.defs';
import {
  calculateMediaDeltaFromChangeRecords,
  emitDiagramMediaReferenceDelta,
} from '../../domains/media/utils/commandMediaReferenceEvents';
import {
  getTranslatedBpmnShapeLabel,
  getTranslatedFlowchartShapeLabel,
} from '../../domains/process/shapes/flowchart/flowchartShapeI18n';

export class ChangeNodeStyleCommand extends BaseNodeCommand {
  private nodeIds: string[];
  private styleField: string;
  private newValue: any;

  private static readonly TEXT_STYLE_FIELDS = new Set([
    'fontSize',
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'textDecoration',
    'textAlign',
    'verticalAlign',
    'textColor',
  ]);

  private static readonly PATCHABLE_SHAPE_STYLE_FIELDS = new Set([
    'fillColor',
    'strokeColor',
    'borderColor',
    'borderWidth',
    'strokeWidth',
    'dash',
    'dashEnabled',
    'textAlign',
    'verticalAlign',
    'textColor',
  ]);

  constructor(nodeIds: string[], styleField: string, newValue: any) {
    const description = `Change ${styleField} to ${JSON.stringify(newValue)} for ${nodeIds.length} nodes`;
    super('Change Style', description);
    this.nodeIds = nodeIds;
    this.styleField = styleField;
    this.newValue = newValue;
  }

  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];

    console.log(
      `[ChangeNodeStyleCommand] Executing for ${this.nodeIds.length} nodes, field: ${this.styleField}, value:`,
      this.newValue,
    );

    const batchUpdates: Array<{ nodeId: string; changes: any }> = [];

    const store = useDiagramStore.getState();
    const storeNodeById = new Map(store.nodes.map((n) => [n.id, n as any]));
    const expandedIds = Array.from(expandNodeIdsWithGroupMembers(this.nodeIds, store.nodes));
    expandedIds.forEach((nodeId) => {
      console.log(`[ChangeNodeStyleCommand] Processing node ${nodeId}`);

      const currentNodeState = this.getMutationManager().getNode(nodeId);
      const storeNodeState = storeNodeById.get(nodeId);
      if (
        ChangeNodeStyleCommand.TEXT_STYLE_FIELDS.has(this.styleField) &&
        !this.isTextCapableNode(currentNodeState as any)
      ) {
        return;
      }

      // Projection-owned locks are structural: the node belongs to a scene
      // rebuilt from canonical records, so an unlock would silently revert on
      // the next rebuild. Refuse at the command layer so no UI entry point
      // (overlay, toolbar toggle, shortcut) can produce the futile write.
      if (
        this.styleField === 'locked' &&
        ((currentNodeState as any)?.lockOwner === 'projection' ||
          storeNodeState?.lockOwner === 'projection')
      ) {
        return;
      }

      const changes: any = {};
      const ensureAttrs = () => {
        if (!changes.attrs) {
          changes.attrs = currentNodeState?.attrs ? { ...currentNodeState.attrs } : {};
        }
        return changes.attrs as Record<string, unknown>;
      };

      switch (this.styleField) {
        case 'stylePatch': {
          const patch = this.isRecord(this.newValue) ? this.newValue : {};
          Object.entries(patch).forEach(([field, value]) => {
            if (!ChangeNodeStyleCommand.PATCHABLE_SHAPE_STYLE_FIELDS.has(field)) return;

            if (field === 'borderColor') {
              changes.strokeColor = value;
              return;
            }

            if (field === 'borderWidth' || field === 'strokeWidth') {
              ensureAttrs().strokeWidth = value;
              changes.strokeWidth = value as number;
              return;
            }

            if (field === 'dash' || field === 'dashEnabled') {
              ensureAttrs()[field] = value;
              return;
            }

            if (field === 'textAlign' || field === 'verticalAlign') {
              ensureAttrs()[field] = value;
              return;
            }

            changes[field] = value;
          });
          break;
        }
        case 'fillColor':
        case 'strokeColor':
        case 'borderColor':
          changes[this.styleField === 'borderColor' ? 'strokeColor' : this.styleField] = this.newValue;
          break;
        case 'borderWidth':
          ensureAttrs()['strokeWidth'] = this.newValue;
          changes.strokeWidth = this.newValue as number;
          break;
        case 'sex':
        case 'gender':
          changes.sex = this.newValue;
          break;
        case 'deceased':
        case 'isDeceased':
          changes.deceased = this.newValue;
          break;
        case 'birthYear':
        case 'birthDate':
          changes.birthYear = this.newValue;
          break;
        case 'deathYear':
        case 'deathDate':
          changes.deathYear = this.newValue;
          break;
        case 'name':
        case 'location':
          changes[this.styleField] = this.newValue;
          break;
        case 'locked':
          changes[this.styleField] = !!this.newValue;
          break;
        case 'shapeType':
        case 'fontSize':
        case 'fontFamily':
        case 'fontWeight':
        case 'fontStyle':
        case 'textDecoration':
        case 'textAlign':
        case 'verticalAlign':
        case 'textColor':
        case 'strokeWidth':
        case 'dash':
        case 'dashEnabled':
          ensureAttrs()[this.styleField] = this.newValue;
          if (this.styleField === 'strokeWidth') {
            changes.strokeWidth = this.newValue as number;
          }
          break;
        default:
          if (!currentNodeState?.attrs) {
            changes.attrs = {};
          } else {
            changes.attrs = { ...currentNodeState.attrs };
          }
          changes.attrs[this.styleField] = this.newValue;
          if (this.styleField === 'variant' && typeof this.newValue === 'string') {
            const nodeType = (currentNodeState as any)?.type;
            const isFlowchartOrBpmn = nodeType === 'flowchart-shape' || nodeType === 'bpmn-shape';
            const isBasic = nodeType === 'basic-shape';
            const isOrgShape = nodeType === 'org-shape';

            const hasUserResized = Boolean((currentNodeState as any)?.attrs?.userResized || (storeNodeState as any)?.attrs?.userResized);
            const currentW =
              (storeNodeState as any)?.w ??
              (storeNodeState as any)?.width ??
              (currentNodeState as any)?.w ??
              (currentNodeState as any)?.width;
            const currentH =
              (storeNodeState as any)?.h ??
              (storeNodeState as any)?.height ??
              (currentNodeState as any)?.h ??
              (currentNodeState as any)?.height;
            const hasFiniteCurrentSize =
              typeof currentW === 'number' && Number.isFinite(currentW) && currentW > 0 &&
              typeof currentH === 'number' && Number.isFinite(currentH) && currentH > 0;
            const shouldPreserveSize = hasUserResized && hasFiniteCurrentSize;

            const resolveDefaultDims = (): { width: number; height: number } => {
              if (isOrgShape) {
                const orgManifest = getShapeManifest('org', this.newValue);
                return orgManifest?.defaultDimensions ?? { width: 160, height: 90 };
              }
              const flowchartManifest = getShapeManifest('flowchart', this.newValue);
              const genogramManifest = getShapeManifest('genogram', this.newValue);
              return (
                flowchartManifest?.defaultDimensions ??
                genogramManifest?.defaultDimensions ??
                (BPMN_SHAPES as Record<string, { width: number; height: number }>)[this.newValue] ??
                { width: 120, height: 80 }
              );
            };

            if (isFlowchartOrBpmn || isBasic || isOrgShape) {
              if (!shouldPreserveSize) {
                const defaultDims = resolveDefaultDims();
                changes.width = defaultDims.width;
                changes.height = defaultDims.height;
                changes.w = defaultDims.width;
                changes.h = defaultDims.height;
              }

              if (isFlowchartOrBpmn) {
                const bpmnDef = (BPMN_SHAPES as Record<string, { text?: string }>)[this.newValue];
                const flowchartDef = (AI_FLOWCHART_SHAPES as Record<string, { text?: string }>)[this.newValue];
                const defaultText = bpmnDef?.text
                  ? getTranslatedBpmnShapeLabel(String(this.newValue))
                  : flowchartDef?.text
                    ? getTranslatedFlowchartShapeLabel(String(this.newValue))
                    : undefined;
                if (typeof defaultText === 'string' && defaultText.length > 0) {
                  changes.text = defaultText;
                  changes.attrs.label = defaultText;
                }
              }
            }
          }
      }

      batchUpdates.push({ nodeId, changes });
      console.log(`[ChangeNodeStyleCommand] Prepared changes for node ${nodeId}:`, changes);
    });

    if (batchUpdates.length > 1) {
      console.log(`[ChangeNodeStyleCommand] Applying batch updates to ${batchUpdates.length} nodes`);
      const batchRecords = (this.getMutationManager() as any).applyBatchNodeChanges(batchUpdates);
      records.push(...batchRecords);
    } else if (batchUpdates.length === 1) {
      const { nodeId, changes } = batchUpdates[0];
      console.log(`[ChangeNodeStyleCommand] Applying single node update to ${nodeId}`);
      const record = this.getMutationManager().applyNodeChange(nodeId, changes);
      if (record) records.push(record);
    }

    return records;
  }

  override undo(): void {
    const forwardDelta = calculateMediaDeltaFromChangeRecords(this.changeRecords);
    super.undo();
    emitDiagramMediaReferenceDelta({
      addedKeys: forwardDelta.removedKeys,
      removedKeys: forwardDelta.addedKeys,
    });
  }

  override redo(): void {
    super.redo();
    const forwardDelta = calculateMediaDeltaFromChangeRecords(this.changeRecords);
    emitDiagramMediaReferenceDelta({
      addedKeys: forwardDelta.addedKeys,
      removedKeys: forwardDelta.removedKeys,
    });
  }

  private isTextCapableNode(node: { type?: string; attrs?: Record<string, unknown> } | null | undefined): boolean {
    if (!node) return false;
    if (node.type === 'text' || node.type === 'sticky-note') return true;
    if (Object.prototype.hasOwnProperty.call(node as any, 'text')) return true;
    if (Object.prototype.hasOwnProperty.call(node as any, 'name')) return true;
    const attrs = node.attrs;
    if (attrs && Object.prototype.hasOwnProperty.call(attrs, 'text')) return true;
    return false;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
